import { Control } from '@yleisradio/areena-types';
import ChevronDown from 'assets/chevronDown.svg';
import ChevronUp from 'assets/chevronUp.svg';
import classNames from 'classnames';
import { isNavigator } from 'components/Controls/Control/typeGuards';
import { Link } from 'components/Link';
import { useBadge } from 'hooks/useBadge';
import Image from 'next/image';
import React, { FunctionComponent } from 'react';
import { defaultLoader, loaderUrl } from 'utils/cloudinary';
import { FavoritesBadge } from './FavoritesBadge';
import styles from './MenuBarItem.module.scss';

const linkTitle = (item: Control, isMenuOpen: boolean) => {
  if (item.tag !== 'browse') {
    return <span>{item.title}</span>;
  }

  return (
    <>
      <span className={styles.browseTitle}>{item.title}</span>
      {isMenuOpen ? (
        <ChevronUp className={styles.browseIcon} />
      ) : (
        <ChevronDown className={styles.browseIcon} />
      )}
    </>
  );
};

type Props = {
  control: Control;
  toggleSubMenu: () => void;
  isMenuOpen: boolean;
  closeSubMenu(): void;
};

export const MenuBarItem: FunctionComponent<Props> = ({
  control,
  toggleSubMenu,
  isMenuOpen,
  closeSubMenu,
}) => {
  const menuBadge = useBadge(control.badgeSource);
  const showMenuBadge = !!menuBadge?.formatted;

  const icon = (control: Control) => (control.icon ? control.icon.id : null);
  const getLogoWidth = (id: string) =>
    ['lasten-areena', 'barnens-arena'].includes(id) ? 106 : 70;

  const children = (
    <>
      {control.image ? (
        <div className={classNames(styles.itemImage, styles[control.image.id])}>
          <Image
            aria-label={control.title}
            alt={control.title || ''}
            loader={defaultLoader}
            src={loaderUrl(control.image)}
            width={getLogoWidth(control.image.id)}
            height={24}
            priority
          />
        </div>
      ) : (
        linkTitle(control, isMenuOpen)
      )}
      {showMenuBadge && <FavoritesBadge favoritesBadge={menuBadge} />}
    </>
  );

  const onClick =
    control.tag === 'browse'
      ? () => {
          toggleSubMenu();
        }
      : undefined;

  return (
    <li key={control.title} className={styles.menuBarItem}>
      {!isNavigator(control) || control.tag === 'browse' ? (
        <button
          className={styles.itemLink}
          data-icon={icon(control)}
          onClick={onClick}
        >
          {children}
        </button>
      ) : (
        <Link
          className={styles.itemLink}
          classNameCurrentPage={styles.itemLinkCurrent}
          pointer={control.destination}
          data-icon={icon(control)}
          onClick={closeSubMenu}
        >
          {children}
        </Link>
      )}
    </li>
  );
};
